import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LottoShake = () => {
  const [selectedGame, setSelectedGame] = useState('lotto');
  const [generatedNumbers, setGeneratedNumbers] = useState([]);
  const [generatedExtraNumbers, setGeneratedExtraNumbers] = useState([]);
  const [isShaking, setIsShaking] = useState(false);
  const [showNumbers, setShowNumbers] = useState(false);
  const [isListening, setIsListening] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const shakeTimeoutRef = useRef(null);

  // Konfiguracja gier
  const gameConfigs = {
    'lotto': {
      name: 'Lotto',
      mainNumbers: { count: 6, max: 49 },
      extraNumbers: null,
      color: 'from-yellow-400 via-orange-500 to-red-500'
    },
    'miniLotto': {
      name: 'Mini Lotto',
      mainNumbers: { count: 5, max: 42 },
      extraNumbers: null,
      color: 'from-green-400 via-emerald-500 to-teal-500'
    },
    'eurojackpot': {
      name: 'Eurojackpot',
      mainNumbers: { count: 5, max: 50 },
      extraNumbers: { count: 2, max: 12 },
      color: 'from-blue-400 via-purple-500 to-indigo-500'
    },
    'multiMulti': {
      name: 'Multi Multi',
      mainNumbers: { count: 20, max: 80 },
      extraNumbers: null,
      color: 'from-purple-400 via-pink-500 to-rose-500'
    },
    'kaskada': {
      name: 'Kaskada',
      mainNumbers: { count: 12, max: 36 },
      extraNumbers: null,
      color: 'from-indigo-400 via-blue-500 to-cyan-500'
    },
    'keno': {
      name: 'Keno',
      mainNumbers: { count: 10, max: 70 },
      extraNumbers: null,
      color: 'from-red-400 via-pink-500 to-purple-500'
    }
  };

  // iOS-compatible shake detection
  useEffect(() => {
    if (!isListening) return;

    const handleDeviceMotion = (event) => {
      const { accelerationIncludingGravity } = event;
      if (!accelerationIncludingGravity) return;

      const { x, y, z } = accelerationIncludingGravity;
      const acceleration = Math.sqrt(x * x + y * y + z * z);

      // Wykryj potrząśnięcie (próg ~12 dla iOS, ~15 dla Android)
      const threshold = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 12 : 15;
      if (acceleration > threshold && !isShaking) {
        handleShake();
      }
    };

    // iOS wymaga pozwolenia na device motion
    const requestPermissionAndListen = async () => {
      if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
        try {
          const permission = await DeviceMotionEvent.requestPermission();
          if (permission === 'granted') {
            window.addEventListener('devicemotion', handleDeviceMotion, { passive: true });
          } else {
            console.log('Device motion permission denied');
          }
        } catch (error) {
          console.log('Error requesting device motion permission:', error);
          // Fallback - dodaj listener bez pozwolenia
          window.addEventListener('devicemotion', handleDeviceMotion, { passive: true });
        }
      } else {
        // Nie-iOS lub starsze wersje iOS
        window.addEventListener('devicemotion', handleDeviceMotion, { passive: true });
      }
    };

    requestPermissionAndListen();
    
    // Dodaj także listener dla klawiatury (do testowania na desktopie)
    const handleKeyPress = (event) => {
      if (event.code === 'Space' && !isShaking) {
        handleShake();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('devicemotion', handleDeviceMotion);
      window.removeEventListener('keydown', handleKeyPress);
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current);
      }
    };
  }, [isListening, isShaking]);

  const generateLottoNumbers = (gameKey) => {
    const game = gameConfigs[gameKey];
    if (!game) return { main: [], extra: [] };

    // Generuj liczby główne
    const mainNumbers = new Set();
    while (mainNumbers.size < game.mainNumbers.count) {
      const randomNum = Math.floor(Math.random() * game.mainNumbers.max) + 1;
      mainNumbers.add(randomNum);
    }
    
    // Generuj liczby dodatkowe (jeśli gra je wymaga)
    const extraNumbers = new Set();
    if (game.extraNumbers) {
      while (extraNumbers.size < game.extraNumbers.count) {
        const randomNum = Math.floor(Math.random() * game.extraNumbers.max) + 1;
        extraNumbers.add(randomNum);
      }
    }
    
    return {
      main: Array.from(mainNumbers).sort((a, b) => a - b),
      extra: Array.from(extraNumbers).sort((a, b) => a - b)
    };
  };

  const handleShake = async () => {
    if (isShaking) return;
    
    setIsShaking(true);
    setIsListening(false);
    setShowNumbers(false);

    // Wibracja telefonu (w React Native użyjesz Vibration.vibrate())
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }

    // Poczekaj na animację potrząśnięcia
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Wygeneruj liczby
    const result = generateLottoNumbers(selectedGame);
    setGeneratedNumbers(result.main);
    setGeneratedExtraNumbers(result.extra);
    setShowNumbers(true);
    setIsShaking(false);

    // NIE pozwalaj na kolejne potrząśnięcie automatycznie - tylko po kliknięciu "Potrząśnij ponownie"
  };

  // Funkcja kopiowania liczb
  const copyNumbers = () => {
    const game = gameConfigs[selectedGame];
    let numbersText = `${game.name}: ${generatedNumbers.join(', ')}`;
    if (generatedExtraNumbers.length > 0) {
      numbersText += ` + ${generatedExtraNumbers.join(', ')}`;
    }
    navigator.clipboard.writeText(numbersText);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 2000);
  };

  // Funkcja dodawania do ulubionych (kompatybilna z sekcją w "Moje Konto")
  const addToFavorites = () => {
    const game = gameConfigs[selectedGame];
    let numbersText = generatedNumbers.join(', ');
    if (generatedExtraNumbers.length > 0) {
      numbersText += ` + ${generatedExtraNumbers.join(', ')}`;
    }
    
    const favorite = {
      id: Date.now(),
      name: `📱 Shake ${game.name} ${new Date().toLocaleDateString('pl-PL')}`,
      set: numbersText,
      game: selectedGame,
      generatorType: 'shake-generator',
      date: new Date().toISOString(),
      mainNumbers: [...generatedNumbers],
      extraNumbers: [...generatedExtraNumbers]
    };
    
    // Zapisz w localStorage (ten sam klucz co inne ulubione)
    const existingFavorites = JSON.parse(localStorage.getItem('favoriteSets') || '[]');
    const updatedFavorites = [favorite, ...existingFavorites];
    localStorage.setItem('favoriteSets', JSON.stringify(updatedFavorites));
    
    // Pokaż komunikat i przekieruj
    alert(`✅ Zestaw "${favorite.name}" został dodany do ulubionych!`);
  };

  const ballVariants = {
    hidden: { 
      y: 300, 
      opacity: 0, 
      scale: 0.5,
      rotate: 0 
    },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 360,
      transition: {
        delay: i * 0.15,
        duration: 0.8,
        type: "spring",
        stiffness: 120,
        damping: 12
      }
    }),
    bounce: {
      y: [0, -15, 0],
      scale: [1, 1.1, 1],
      transition: {
        duration: 0.6,
        repeat: 1,
        ease: "easeInOut"
      }
    }
  };

  const shakeVariants = {
    shake: {
      x: [0, -5, 5, -5, 5, 0],
      y: [0, -2, 2, -2, 2, 0],
      rotate: [0, -1, 1, -1, 1, 0],
      scale: [1, 1.02, 0.98, 1.02, 0.98, 1],
      transition: {
        duration: 0.8,
        ease: "easeInOut"
      }
    }
  };

  const glowVariants = {
    glow: {
      boxShadow: [
        "0 0 8px rgba(255, 255, 255, 0.2)",
        "0 0 15px rgba(255, 255, 255, 0.3)",
        "0 0 8px rgba(255, 255, 255, 0.2)"
      ],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-violet-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      
      {/* Tło z animowanymi cząsteczkami */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.2, 0.5, 0.2],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Główny kontener z animacją potrząśnięcia */}
      <motion.div
        className="flex flex-col items-center justify-center w-full max-w-md relative z-10"
        variants={shakeVariants}
        animate={isShaking ? "shake" : ""}
      >
        
        {/* Nagłówek */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent mb-4 drop-shadow-lg">
            📱 Shake to Generate
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Lotto Numbers
          </h2>
        </motion.div>

        {/* Dropdown wyboru gry */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-8 w-full max-w-xs"
        >
          <label className="block text-white/90 text-sm font-medium mb-3 text-center">
            Wybierz grę:
          </label>
          <motion.select
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            className="w-full bg-white/10 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            whileFocus={{ scale: 1.02 }}
          >
            {Object.entries(gameConfigs).map(([key, game]) => (
              <option key={key} value={key} className="bg-gray-800 text-white">
                {game.name}
              </option>
            ))}
          </motion.select>
        </motion.div>

        {/* Instrukcja */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-white/90 text-lg md:text-xl font-medium mb-2">
            {isShaking ? "🔄 Generuję liczby..." : "Potrząśnij telefonem,"}
          </p>
          <p className="text-white/70 text-base md:text-lg">
            {isShaking ? "Poczekaj chwilę..." : `aby wylosować ${gameConfigs[selectedGame].name}!`}
          </p>
          <p className="text-white/50 text-sm mt-2">
            (Na desktopie naciśnij SPACJĘ)
          </p>
        </motion.div>

        {/* Telefon ikona z animacją */}
        <motion.div
          className="mb-12"
          animate={isShaking ? {
            rotate: [0, -10, 10, -10, 10, 0],
            scale: [1, 1.1, 0.9, 1.1, 0.9, 1]
          } : {
            y: [0, -10, 0],
            rotate: [0, 2, -2, 0]
          }}
          transition={{
            duration: isShaking ? 0.8 : 3,
            repeat: isShaking ? 0 : Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="text-8xl filter drop-shadow-lg">
            📱
          </div>
        </motion.div>

        {/* Wyniki - kulki z liczbami */}
        <AnimatePresence>
          {showNumbers && generatedNumbers.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full"
            >
              <motion.h3 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-white text-center text-xl font-bold mb-6"
              >
                🎲 {gameConfigs[selectedGame].name} - Twoje liczby:
              </motion.h3>
              
              {/* Liczby główne */}
              <div className="mb-4">
                {gameConfigs[selectedGame].extraNumbers && (
                  <p className="text-white/80 text-center text-sm mb-3">
                    Liczby główne:
                  </p>
                )}
                <div className="flex flex-wrap justify-center gap-3">
                  {generatedNumbers.map((number, index) => (
                    <motion.div
                      key={number}
                      custom={index}
                      variants={ballVariants}
                      initial="hidden"
                      animate={["visible", "bounce"]}
                      className="relative"
                    >
                      <motion.div
                        variants={glowVariants}
                        animate="glow"
                        className={`w-14 h-14 rounded-full bg-gradient-to-br ${gameConfigs[selectedGame].color} flex items-center justify-center text-white font-bold text-lg shadow-lg border-2 border-white/30 relative overflow-hidden`}
                      >
                        {/* Efekt shine - zmniejszony */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          animate={{
                            x: ["-100%", "100%"],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            repeatDelay: 5,
                            ease: "easeInOut"
                          }}
                          style={{
                            transform: "skewX(-20deg)",
                          }}
                        />
                        <span className="relative z-10 drop-shadow-sm">
                          {number}
                        </span>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Liczby dodatkowe (jeśli istnieją) */}
              {generatedExtraNumbers.length > 0 && (
                <div className="mb-6">
                  <p className="text-white/80 text-center text-sm mb-3">
                    Liczby dodatkowe:
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {generatedExtraNumbers.map((number, index) => (
                      <motion.div
                        key={`extra-${number}`}
                        custom={index + generatedNumbers.length}
                        variants={ballVariants}
                        initial="hidden"
                        animate={["visible", "bounce"]}
                        className="relative"
                      >
                        <motion.div
                          variants={glowVariants}
                          animate="glow"
                          className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow-lg border-2 border-white/30 relative overflow-hidden"
                        >
                          <span className="relative z-10 drop-shadow-sm">
                            {number}
                          </span>
                        </motion.div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Przyciski akcji */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  onClick={copyNumbers}
                  className="bg-white/20 hover:bg-white/30 text-white font-medium py-3 px-6 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>📋</span>
                  <span>Kopiuj</span>
                </motion.button>
                
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 }}
                  onClick={addToFavorites}
                  className="bg-white/20 hover:bg-white/30 text-white font-medium py-3 px-6 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>⭐</span>
                  <span>Ulubione</span>
                </motion.button>

                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 }}
                  onClick={() => {
                    setShowNumbers(false);
                    setGeneratedNumbers([]);
                    setGeneratedExtraNumbers([]);
                    setIsListening(true);
                  }}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  🔄 Potrząśnij ponownie
                </motion.button>
              </div>

              {/* Komunikat o skopiowaniu */}
              <AnimatePresence>
                {showCopySuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-4 p-3 bg-green-500/20 border border-green-400/30 rounded-lg text-center"
                  >
                    <p className="text-green-300 text-sm">✅ Skopiowano do schowka!</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading indicator podczas potrząśnięcia */}
        {isShaking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="bg-black/50 backdrop-blur-sm rounded-3xl p-8 flex flex-col items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full mb-4"
              />
              <p className="text-white font-medium">Mieszam liczby...</p>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default LottoShake;
